import { Typography } from '@euler/app/components/typography';
import { BottomPanel } from '@euler/components/BottomPanel';
import { couponBenefitText } from '@euler/functions/apdm/couponRedemption';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ApdmCouponCampaignSummary } from '@euler/model/apdm';
import { Ticket, X } from 'lucide-react-native';
import { memo, useEffect, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { ChatCardBadge } from '../components/ChatCard';
import {
  couponPeriodText,
  kCouponStatusLabels,
} from '../components/couponText';
import { aiColors } from '../theme';

/** Everything about one coupon campaign, opened from a card's detail link. */
export const CouponCampaignDetailSheet = memo(
  ({
    coupon: open,
    onClose,
  }: {
    coupon: ApdmCouponCampaignSummary | null;
    onClose: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    // The panel animates out after `open` clears; keep its content until
    // then.
    const [coupon, setCoupon] = useState(open);
    useEffect(() => {
      if (open) setCoupon(open);
    }, [open]);
    const row = (label: string, value: string) => (
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: rpx(12),
        }}
      >
        <Typography.Text size={rpx(13)} color={aiColors.mutedForeground}>
          {label}
        </Typography.Text>
        <Typography.Text
          medium
          size={rpx(13)}
          color={aiColors.foreground}
          style={{ flexShrink: 1, textAlign: 'right' }}
        >
          {value}
        </Typography.Text>
      </View>
    );
    return (
      <BottomPanel visible={open != null} onClose={onClose}>
        {coupon && (
          <View style={{ flex: 1, backgroundColor: aiColors.card }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(10),
                padding: rpx(16),
                borderBottomWidth: 1,
                borderBottomColor: aiColors.border,
              }}
            >
              <View
                style={{
                  width: rpx(36),
                  height: rpx(36),
                  borderRadius: rpx(10),
                  backgroundColor: aiColors.primary,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Ticket size={18} color="#fff" />
              </View>
              <View style={{ flex: 1 }}>
                <Typography.Text
                  bold
                  size={rpx(16)}
                  color={aiColors.foreground}
                >
                  {coupon.title}
                </Typography.Text>
                <Typography.Text
                  size={rpx(12)}
                  color={aiColors.mutedForeground}
                >
                  {couponPeriodText(coupon)}
                </Typography.Text>
              </View>
              <ChatCardBadge label={kCouponStatusLabels[coupon.status]} />
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="关闭"
                onPress={onClose}
                hitSlop={8}
              >
                <X size={20} color={aiColors.mutedForeground} />
              </Pressable>
            </View>
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ padding: rpx(16), gap: rpx(14) }}
            >
              {coupon.description ? (
                <Typography.Text
                  size={rpx(13)}
                  lineHeight={rpx(20)}
                  color={aiColors.foreground}
                >
                  {coupon.description}
                </Typography.Text>
              ) : null}
              <View style={{ gap: rpx(8) }}>
                <Typography.Text
                  medium
                  size={rpx(13)}
                  color={aiColors.foreground}
                >
                  包含 {coupon.items.length} 项权益
                </Typography.Text>
                {coupon.items.map((item, index) => (
                  <View
                    key={index}
                    style={{
                      padding: rpx(12),
                      borderRadius: rpx(10),
                      backgroundColor: aiColors.muted,
                      gap: rpx(2),
                    }}
                  >
                    <Typography.Text
                      medium
                      size={rpx(14)}
                      color={aiColors.foreground}
                    >
                      {item.label}
                    </Typography.Text>
                    <Typography.Text
                      size={rpx(12)}
                      color={aiColors.mutedForeground}
                    >
                      {couponBenefitText(item.benefit)} · 每位客户
                      {item.qty}
                      {item.qtyUnit ?? '次'}
                    </Typography.Text>
                  </View>
                ))}
              </View>
              <View style={{ gap: rpx(8) }}>
                {row(
                  '剩余库存',
                  coupon.stockLeft == null
                    ? '不限量'
                    : `${coupon.stockLeft} 份`,
                )}
                {row('已核销', `${coupon.redeemedCount}`)}
                {row('有效期', couponPeriodText(coupon))}
              </View>
              <Typography.Text
                size={rpx(11)}
                lineHeight={rpx(17)}
                color={aiColors.mutedForeground}
              >
                每位客户会收到带专属二维码的海报，到店出示后由顾问扫码核销。
              </Typography.Text>
            </ScrollView>
          </View>
        )}
      </BottomPanel>
    );
  },
);
